import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  ALL_OHLC_RESOLUTIONS,
  DEFAULT_OHLC_RESOLUTIONS,
  type DateRangeOrLookback,
  HistoricalPriceChart,
  MixpanelEvent,
  MixpanelEventSource,
  type ResolutionEnumNoPrefix,
  Spinner,
  useMixpanel,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-enterprise';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

export const OHLCVChart = ({
  showSymbolSelector,
  symbol,
  resolution,
  annotations,
  chartType = 'candlestick',
  markets,
  leftAlignedItems,
  rightAlignedItems,
  onSymbolChange,
  onResolutionChange,
  onAnnotationsChange,
  onChartTypeChange,
}: {
  showSymbolSelector?: boolean;
  symbol: string;
  resolution: ResolutionEnumNoPrefix;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
  markets: string[];
  leftAlignedItems?: ReactNode;
  rightAlignedItems?: ReactNode;
  onSymbolChange: ((symbol: string) => void) | undefined;
  onResolutionChange: (resolution: ResolutionEnumNoPrefix) => void;
  onAnnotationsChange: (annotations: AgAnnotation[] | undefined) => void;
  onChartTypeChange: (chartType: AgPriceVolumeChartType) => void;
}) => {
  const mixpanel = useMixpanel();
  const { enableNonNativeOHLCResolutions } = useFeatureFlag();
  const [dateRange, setDateRange] = useState<DateRangeOrLookback | undefined>();

  const handleSymbolChange = useCallback(
    (symbol?: string) => {
      if (symbol) {
        mixpanel.track(MixpanelEvent.ChangeSymbol, { source: MixpanelEventSource.CandleSticks });
        onSymbolChange?.(symbol);
      }
    },
    [onSymbolChange, mixpanel]
  );

  const handleAnnotationsChange = useCallback(
    (newAnnotations?: AgAnnotation[] | undefined) => {
      mixpanel.track(MixpanelEvent.ChangeAnnotations, { source: MixpanelEventSource.CandleSticks });
      onAnnotationsChange(newAnnotations);
    },
    [onAnnotationsChange, mixpanel]
  );

  const handleChartTypeChange = useCallback(
    (newChartType: AgPriceVolumeChartType) => {
      mixpanel.track(MixpanelEvent.ChangeChartType, { source: MixpanelEventSource.CandleSticks });
      onChartTypeChange(newChartType);
    },
    [onChartTypeChange, mixpanel]
  );

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangeOrLookback) => {
      mixpanel.track(MixpanelEvent.ChangeDateRange, { source: MixpanelEventSource.CandleSticks });
      setDateRange(newDateRange);
    },
    [setDateRange, mixpanel]
  );

  return symbol ? (
    <HistoricalPriceChart
      key={`${symbol}-${resolution}`}
      symbol={symbol}
      markets={markets}
      onChangeSymbol={showSymbolSelector ? handleSymbolChange : undefined}
      resolution={resolution}
      availableResolutions={enableNonNativeOHLCResolutions ? ALL_OHLC_RESOLUTIONS : DEFAULT_OHLC_RESOLUTIONS}
      onResolutionChange={onResolutionChange}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      onAnnotationsChange={handleAnnotationsChange}
      annotations={annotations}
      chartType={chartType}
      onChartTypeChange={handleChartTypeChange}
      showVolume={true}
      leftAlignedItems={leftAlignedItems}
      rightAlignedItems={rightAlignedItems}
      h="100%"
    />
  ) : (
    <Spinner />
  );
};
